import { Color, Mat4, Quat, Vec3 } from 'playcanvas';

import { AnimTrack } from './anim-track';
import { Element } from './element';
import { reparentPreserveWorld } from './hierarchy';
import { IndexRanges, sortedPredicate } from './index-ranges';
import { Mesh } from './mesh';
import { Pivot } from './pivot';
import { Scene } from './scene';
import { Splat } from './splat';
import { State } from './splat-state';
import { splitSplatBySelection } from './split';
import { Transform } from './transform';

interface EditOp {
    name: string;
    do(): void | Promise<void>;
    undo(): void | Promise<void>;
    destroy?(): void;
}

const enum BitOp {
    SET,
    CLEAR,
    TOGGLE
}

class StateOp {
    splat: Splat;
    ranges: IndexRanges;
    mask: number;
    op: BitOp;
    updateFlags: number;

    constructor(splat: Splat, ranges: IndexRanges, mask: number, op: BitOp, updateFlags = State.selected) {
        this.splat = splat;
        this.ranges = ranges;
        this.mask = mask;
        this.op = op;
        this.updateFlags = updateFlags;
    }

    private apply(op: BitOp) {
        const state = this.splat.splatData.getProp('state') as Uint8Array;
        const { mask } = this;

        switch (op) {
            case BitOp.SET:
                this.ranges.forEach((i) => {
                    state[i] |= mask;
                });
                break;
            case BitOp.CLEAR:
                this.ranges.forEach((i) => {
                    state[i] &= ~mask;
                });
                break;
            case BitOp.TOGGLE:
                this.ranges.forEach((i) => {
                    state[i] ^= mask;
                });
                break;
        }
    }

    async do() {
        this.apply(this.op);
        await this.splat.updateState(this.updateFlags);
    }

    async undo() {
        const undoOp = this.op === BitOp.TOGGLE ? BitOp.TOGGLE :
            this.op === BitOp.SET ? BitOp.CLEAR : BitOp.SET;
        this.apply(undoOp);
        await this.splat.updateState(this.updateFlags);
    }

    destroy() {
        this.splat = null;
        this.ranges = null;
    }
}

class SelectAllOp extends StateOp {
    name = 'selectAll';

    constructor(splat: Splat) {
        const state = splat.splatData.getProp('state') as Uint8Array;
        super(splat, IndexRanges.fromPredicate(splat.splatData.numSplats, i => state[i] === 0), State.selected, BitOp.SET);
    }
}

class SelectNoneOp extends StateOp {
    name = 'selectNone';

    constructor(splat: Splat) {
        const state = splat.splatData.getProp('state') as Uint8Array;
        super(splat, IndexRanges.fromPredicate(splat.splatData.numSplats, i => state[i] === State.selected), State.selected, BitOp.CLEAR);
    }
}

class SelectInvertOp extends StateOp {
    name = 'selectInvert';

    constructor(splat: Splat) {
        const state = splat.splatData.getProp('state') as Uint8Array;
        super(splat, IndexRanges.fromPredicate(splat.splatData.numSplats, i => (state[i] & (State.locked | State.deleted)) === 0), State.selected, BitOp.TOGGLE);
    }
}

class SelectOp extends StateOp {
    name = 'selectOp';

    constructor(splat: Splat, op: 'add' | 'remove' | 'set', filter: ((i: number) => boolean) | Uint32Array) {
        const splatData = splat.splatData;
        const state = splatData.getProp('state') as Uint8Array;
        const bitOp = op === 'add' ? BitOp.SET : op === 'remove' ? BitOp.CLEAR : BitOp.TOGGLE;

        // wrap sorted IDs in a cursor-based predicate
        const pred = filter instanceof Uint32Array ? sortedPredicate(filter) : filter;

        const preds = {
            add: (i: number) => pred(i) && state[i] === 0,
            remove: (i: number) => pred(i) && state[i] === State.selected,
            set: (i: number) => (state[i] === State.selected) !== pred(i)
        };

        super(splat, IndexRanges.fromPredicate(splatData.numSplats, preds[op]), State.selected, bitOp);
    }
}

class HideSelectionOp extends StateOp {
    name = 'hideSelection';

    constructor(splat: Splat) {
        const state = splat.splatData.getProp('state') as Uint8Array;
        super(splat, IndexRanges.fromPredicate(splat.splatData.numSplats, i => state[i] === State.selected), State.locked, BitOp.SET, State.locked);
    }
}

class UnhideAllOp extends StateOp {
    name = 'unhideAll';

    constructor(splat: Splat) {
        const state = splat.splatData.getProp('state') as Uint8Array;
        super(splat, IndexRanges.fromPredicate(splat.splatData.numSplats, i => (state[i] & (State.locked | State.deleted)) === State.locked), State.locked, BitOp.CLEAR, State.locked);
    }
}

class DeleteSelectionOp extends StateOp {
    name = 'deleteSelection';

    constructor(splat: Splat) {
        const state = splat.splatData.getProp('state') as Uint8Array;
        super(splat, IndexRanges.fromPredicate(splat.splatData.numSplats, i => state[i] === State.selected), State.deleted, BitOp.SET, State.deleted);
    }
}

class ResetOp extends StateOp {
    name = 'reset';

    constructor(splat: Splat) {
        const state = splat.splatData.getProp('state') as Uint8Array;
        super(splat, IndexRanges.fromPredicate(splat.splatData.numSplats, i => (state[i] & State.deleted) !== 0), State.deleted, BitOp.CLEAR, State.deleted);
    }
}

// op for modifying a splat transform
class EntityTransformOp {
    name = 'entityTransform';
    splat: Splat;
    oldt: Transform;
    newt: Transform;

    constructor(options: { splat: Splat, oldt: Transform, newt: Transform }) {
        this.splat = options.splat;
        this.oldt = options.oldt;
        this.newt = options.newt;
    }

    do() {
        this.splat.move(this.newt.position, this.newt.rotation, this.newt.scale);
    }

    undo() {
        this.splat.move(this.oldt.position, this.oldt.rotation, this.oldt.scale);
    }

    destroy() {
        this.splat = null;
        this.oldt = null;
        this.newt = null;
    }
}

// op for modifying a splat's import normalization (child rotation)
class SplatImportNormalizationOp {
    name = 'splatImportNormalization';
    splat: Splat;
    oldRot: Quat;
    newRot: Quat;

    constructor(options: { splat: Splat, oldRot: Quat, newRot: Quat }) {
        this.splat = options.splat;
        this.oldRot = new Quat(options.oldRot.x, options.oldRot.y, options.oldRot.z, options.oldRot.w);
        this.newRot = new Quat(options.newRot.x, options.newRot.y, options.newRot.z, options.newRot.w);
    }

    do() {
        this.splat.setImportNormalization(this.newRot);
    }

    undo() {
        this.splat.setImportNormalization(this.oldRot);
    }

    destroy() {
        this.splat = null;
        this.oldRot = null;
        this.newRot = null;
    }
}

const mat = new Mat4();

// op for modifying a subset of individual splats
class SplatsTransformOp {
    name = 'splatsTransform';

    splat: Splat;
    transform: Mat4;
    paletteMap: Map<number, number>;

    constructor(options: { splat: Splat, transform: Mat4, paletteMap: Map<number, number> }) {
        this.splat = options.splat;
        this.transform = options.transform;
        this.paletteMap = options.paletteMap;
    }

    async do() {
        const { splat, transform, paletteMap } = this;
        const state = splat.splatData.getProp('state') as Uint8Array;
        const indices = splat.transformTexture.lock() as Uint16Array;

        // update splat transform palette indices
        for (let i = 0; i < state.length; ++i) {
            if (state[i] === State.selected) {
                indices[i] = paletteMap.get(indices[i]);
            }
        }

        splat.transformTexture.unlock();

        splat.transformPalette.alloc(paletteMap.size);

        // update transform palette
        const { transformPalette } = splat;
        this.paletteMap.forEach((newIdx, oldIdx) => {
            transformPalette.getTransform(oldIdx, mat);
            mat.mul2(transform, mat);
            transformPalette.setTransform(newIdx, mat);
        });

        await splat.updatePositions();
    }

    async undo() {
        const { splat, paletteMap } = this;
        const state = splat.splatData.getProp('state') as Uint8Array;
        const indices = splat.transformTexture.lock() as Uint16Array;

        // invert the palette map
        const inverseMap = new Map<number, number>();
        paletteMap.forEach((newIdx, oldIdx) => {
            inverseMap.set(newIdx, oldIdx);
        });

        // restore the original transform indices
        for (let i = 0; i < state.length; ++i) {
            if (state[i] === State.selected) {
                indices[i] = inverseMap.get(indices[i]);
            }
        }

        splat.transformTexture.unlock();

        splat.transformPalette.free(paletteMap.size);

        await splat.updatePositions();
    }

    destroy() {
        this.splat = null;
        this.transform = null;
        this.paletteMap = null;
    }
}

class PlacePivotOp {
    name = 'setPivot';
    pivot: Pivot;
    oldt: Transform;
    newt: Transform;

    constructor(options: { pivot: Pivot, oldt: Transform, newt: Transform }) {
        this.pivot = options.pivot;
        this.oldt = options.oldt;
        this.newt = options.newt;
    }

    do() {
        this.pivot.place(this.newt);
    }

    undo() {
        this.pivot.place(this.oldt);
    }
}

class SetPivotOffsetOp {
    name = 'setPivotOffset';
    element: Element;
    oldOffset: Vec3;
    newOffset: Vec3;

    constructor(options: { element: Element; oldOffset: Vec3; newOffset: Vec3 }) {
        this.element = options.element;
        // Clone to avoid future mutation affecting undo/redo.
        this.oldOffset = new Vec3(options.oldOffset.x, options.oldOffset.y, options.oldOffset.z);
        this.newOffset = new Vec3(options.newOffset.x, options.newOffset.y, options.newOffset.z);
    }

    do() {
        this.element.pivotOffset.copy(this.newOffset);
        this.element.scene?.events.fire('pivot.changed', this.element);
    }

    undo() {
        this.element.pivotOffset.copy(this.oldOffset);
        this.element.scene?.events.fire('pivot.changed', this.element);
    }

    destroy() {
        this.element = null;
        this.oldOffset = null;
        this.newOffset = null;
    }
}

type ColorAdjustment = {
    tintClr?: Color
    temperature?: number,
    saturation?: number,
    brightness?: number,
    blackPoint?: number,
    whitePoint?: number,
    transparency?: number
};

class SetSplatColorAdjustmentOp {
    name: 'setSplatColor';
    splat: Splat;

    newState: ColorAdjustment;
    oldState: ColorAdjustment;

    constructor(options: { splat: Splat, oldState: ColorAdjustment, newState: ColorAdjustment }) {
        const { splat, oldState, newState } = options;
        this.splat = splat;
        this.oldState = oldState;
        this.newState = newState;
    }

    do() {
        const { splat } = this;
        const { tintClr, temperature, saturation, brightness, blackPoint, whitePoint, transparency } = this.newState;
        if (tintClr) splat.tintClr = tintClr;
        if (temperature !== null) splat.temperature = temperature;
        if (saturation !== null) splat.saturation = saturation;
        if (brightness !== null) splat.brightness = brightness;
        if (blackPoint !== null) splat.blackPoint = blackPoint;
        if (whitePoint !== null) splat.whitePoint = whitePoint;
        if (transparency !== null) splat.transparency = transparency;
    }

    undo() {
        const { splat } = this;
        const { tintClr, temperature, saturation, brightness, blackPoint, whitePoint, transparency } = this.oldState;
        if (tintClr) splat.tintClr = tintClr;
        if (temperature !== null) splat.temperature = temperature;
        if (saturation !== null) splat.saturation = saturation;
        if (brightness !== null) splat.brightness = brightness;
        if (blackPoint !== null) splat.blackPoint = blackPoint;
        if (whitePoint !== null) splat.whitePoint = whitePoint;
        if (transparency !== null) splat.transparency = transparency;
    }
}

// Snapshot-based undo/redo for animation track edits.
// Captures the full track state before and after a mutation.
class AnimTrackEditOp {
    name: string;
    track: AnimTrack;
    before: unknown;
    after: unknown;

    constructor(name: string, track: AnimTrack, before: unknown, after: unknown) {
        this.name = name;
        this.track = track;
        this.before = before;
        this.after = after;
    }

    do() {
        this.track.restore(this.after);
    }

    undo() {
        this.track.restore(this.before);
    }
}

class MultiOp {
    name = 'multiOp';
    ops: EditOp[];

    constructor(ops: EditOp[]) {
        this.ops = ops;
    }

    async do() {
        for (const op of this.ops) {
            await op.do();
        }
    }

    async undo() {
        for (const op of this.ops) {
            await op.undo();
        }
    }

    destroy() {
        this.ops.forEach((op) => {
            if ('destroy' in op && typeof (op as any).destroy === 'function') {
                (op as any).destroy();
            }
        });
    }
}

class AddSplatOp {
    name: 'addSplat';
    scene: Scene;
    splat: Splat;

    constructor(scene: Scene, splat: Splat) {
        this.scene = scene;
        this.splat = splat;
    }

    async do() {
        await this.scene.add(this.splat);
    }

    undo() {
        this.scene.remove(this.splat);
    }

    destroy() {
        this.splat.destroy();
    }
}

class SplatRenameOp {
    name = 'splatRename';
    splat: Splat;
    oldName: string;
    newName: string;

    constructor(splat: Splat, newName: string) {
        this.splat = splat;
        this.oldName = splat.name;
        this.newName = newName;
    }

    do() {
        this.splat.name = this.newName;
    }

    undo() {
        this.splat.name = this.oldName;
    }
}

class MeshRenameOp {
    name = 'meshRename';
    mesh: Mesh;
    oldName: string;
    newName: string;

    constructor(mesh: Mesh, newName: string) {
        this.mesh = mesh;
        this.oldName = mesh.name;
        this.newName = newName;
    }

    do() {
        this.mesh.name = this.newName;
    }

    undo() {
        this.mesh.name = this.oldName;
    }
}

// Undoable add/remove for duplicates/instances created at runtime.
// C.5: Duplicate System.
class DuplicateElementOp implements EditOp {
    name = 'duplicateElement';

    scene: Scene | null;
    private factory: (() => Promise<Element>) | null;
    newElement: Element | null = null;
    private failed = false;

    constructor(scene: Scene, factory: () => Promise<Element>) {
        this.scene = scene;
        this.factory = factory;
    }

    async do() {
        if (this.failed) return;

        if (!this.scene || !this.factory) {
            this.failed = true;
            return;
        }

        try {
            if (!this.newElement) {
                this.newElement = await this.factory();
            }
            if (this.newElement && this.newElement.scene !== this.scene) {
                await this.scene.add(this.newElement);
            }
        } catch (e: any) {
            this.failed = true;

            // Best-effort rollback/cleanup: Scene.add may have partially attached the element
            // before throwing from element.add(). Try to remove and destroy so we don't leave
            // half-added elements hanging around.
            try {
                if (this.scene && this.newElement) {
                    if (this.newElement.scene === this.scene) {
                        try {
                            this.scene.remove(this.newElement);
                        } catch {
                            // ignore
                        }
                    }

                    try {
                        this.newElement.destroy();
                    } catch {
                        // ignore
                    }
                }
            } catch {
                // ignore
            }

            const msg = e?.message ? String(e.message) : 'Duplicate failed';
            try {
                this.scene.events.fire('status.text', msg, { timeoutMs: 3500 });
            } catch {
                // ignore
            }
            console.warn('DuplicateElementOp failed', e);
        }
    }

    undo() {
        if (this.failed) return;
        if (!this.scene) return;
        if (this.newElement && this.newElement.scene === this.scene) {
            this.scene.remove(this.newElement);
        }
    }

    destroy() {
        // If the op is being discarded while the element is NOT in-scene (i.e. it was undone
        // or never created), clean up its resources.
        if (this.newElement && !this.newElement.scene) {
            try {
                this.newElement.destroy();
            } catch (e) {
                // ignore
            }
        }

        this.newElement = null;
        this.scene = null;
        this.factory = null;
    }
}

// C.6: Split a splat into (original minus selection) + (new splat with selection).
// Undo restores the original state and removes the new splat.
class SplitSplatOp implements EditOp {
    name = 'splitSplat';

    private scene: Scene | null;
    private splat: Splat | null;

    private beforeState: Uint8Array | null = null;
    private selectedIndices: Uint32Array | null = null;

    newSplat: Splat | null = null;

    private parentAtSplit: Element | null = null;
    private worldPos: Vec3 | null = null;
    private worldRot: Quat | null = null;
    private worldScale: Vec3 | null = null;

    private failed = false;

    constructor(scene: Scene, splat: Splat) {
        this.scene = scene;
        this.splat = splat;
    }

    private captureSelectionFromState(state: Uint8Array): Uint32Array {
        const ids: number[] = [];
        for (let i = 0; i < state.length; i++) {
            if (state[i] === State.selected) ids.push(i);
        }
        return new Uint32Array(ids);
    }

    private showStatus(text: string) {
        try {
            this.scene?.events?.fire?.('status.text', text, { timeoutMs: 3000 });
        } catch {
            // ignore
        }
    }

    private applyDeleteAndClearSelection() {
        const splat = this.splat;
        const sel = this.selectedIndices;
        if (!splat || !sel) return;

        const state = splat.splatData.getProp('state') as Uint8Array;
        for (let i = 0; i < sel.length; i++) {
            const idx = sel[i];
            state[idx] |= State.deleted;
            state[idx] &= ~State.selected;
        }
    }

    async do() {
        if (this.failed) return;
        if (!this.scene || !this.splat) {
            this.failed = true;
            return;
        }

        const scene = this.scene;
        const splat = this.splat;

        // First run: snapshot selection + state and construct the new splat.
        if (!this.beforeState) {
            const state = splat.splatData.getProp('state') as Uint8Array;
            this.beforeState = state.slice(0);
            this.selectedIndices = this.captureSelectionFromState(state);

            // Guardrails.
            const selectedCount = this.selectedIndices.length;
            const activeCount = splat.numSplats;
            if (selectedCount === 0) {
                this.failed = true;
                this.showStatus('Split Selected: nothing selected');
                return;
            }
            if (activeCount > 0 && selectedCount >= activeCount) {
                this.failed = true;
                this.showStatus('Split Selected: cannot split when all splats are selected');
                return;
            }

            // Snapshot hierarchy + transform so redo is deterministic.
            this.parentAtSplit = (splat as any).parentElement ?? null;
            try {
                this.worldPos = splat.entity.getPosition().clone();
                this.worldRot = splat.entity.getRotation().clone();
                const s = (splat.entity as any).getScale?.();
                this.worldScale = (s ? s.clone() : splat.entity.getLocalScale().clone());
            } catch {
                this.worldPos = new Vec3(0, 0, 0);
                this.worldRot = new Quat(0, 0, 0, 1);
                this.worldScale = new Vec3(1, 1, 1);
            }

            try {
                const newSplat = await splitSplatBySelection(splat, scene, this.selectedIndices);
                if (!newSplat) {
                    this.failed = true;
                    this.showStatus('Split Selected: unavailable');
                    return;
                }
                this.newSplat = newSplat;
            } catch (e) {
                this.failed = true;
                console.warn('SplitSplatOp: split failed', e);
                this.showStatus('Split Selected failed: see console');

                // Best-effort cleanup.
                try {
                    this.newSplat?.destroy?.();
                } catch {
                    // ignore
                }
                this.newSplat = null;
                return;
            }
        }

        // Apply mutation to original.
        this.applyDeleteAndClearSelection();
        await splat.updateState(State.deleted);

        // Add (or re-add) the new splat.
        if (this.newSplat) {
            if (this.newSplat.scene !== scene) {
                await scene.add(this.newSplat);
            }

            // Restore snapped world transform.
            try {
                this.newSplat.entity.setPosition(this.worldPos);
                this.newSplat.entity.setRotation(this.worldRot);
                this.newSplat.entity.setLocalScale(this.worldScale);
            } catch {
                // ignore
            }

            // Reparent under the same logical parent as the original, preserving world.
            if (this.parentAtSplit) {
                try {
                    reparentPreserveWorld(this.newSplat as any, this.parentAtSplit as any, scene);
                } catch {
                    // ignore
                }
            }
        }
    }

    async undo() {
        if (this.failed) return;
        if (!this.scene || !this.splat || !this.beforeState) return;

        const scene = this.scene;
        const splat = this.splat;

        // Remove the new splat from scene (but keep it alive for redo).
        if (this.newSplat && this.newSplat.scene === scene) {
            try {
                scene.remove(this.newSplat);
            } catch {
                // ignore
            }
        }

        // Detach from logical hierarchy so redo can reparent deterministically.
        if (this.newSplat) {
            try {
                const oldParent = (this.newSplat as any).parentElement as Element | null;
                if (oldParent) {
                    const kids = (oldParent as any).childElements as Element[] | undefined;
                    if (Array.isArray(kids)) {
                        for (let i = kids.length - 1; i >= 0; i--) {
                            if (kids[i] === (this.newSplat as any)) kids.splice(i, 1);
                        }
                    }
                }
                (this.newSplat as any).parentElement = null;
            } catch {
                // ignore
            }
        }

        // Restore original per-splat state.
        const state = splat.splatData.getProp('state') as Uint8Array;
        state.set(this.beforeState);
        await splat.updateState(State.deleted);
    }

    destroy() {
        // If the op is discarded while the split splat isn't in the scene, free its resources.
        if (this.newSplat && !this.newSplat.scene) {
            try {
                this.newSplat.destroy();
            } catch {
                // ignore
            }
        }

        this.scene = null;
        this.splat = null;
        this.beforeState = null;
        this.selectedIndices = null;
        this.newSplat = null;
        this.parentAtSplit = null;
        this.worldPos = null;
        this.worldRot = null;
        this.worldScale = null;
    }
}

export {
    EditOp,
    SelectAllOp,
    SelectNoneOp,
    SelectInvertOp,
    SelectOp,
    HideSelectionOp,
    UnhideAllOp,
    DeleteSelectionOp,
    ResetOp,
    EntityTransformOp,
    SplatImportNormalizationOp,
    SplatsTransformOp,
    PlacePivotOp,
    SetPivotOffsetOp,
    ColorAdjustment,
    SetSplatColorAdjustmentOp,
    AnimTrackEditOp,
    MultiOp,
    AddSplatOp,
    SplatRenameOp,
    MeshRenameOp,
    DuplicateElementOp,
    SplitSplatOp
};
